import { PRODUCT_ASPECT_TYPES } from '@/constants/positions'

export const ASPECT_TYPE_ORDER_FOR_STAIRCASE = {
  [PRODUCT_ASPECT_TYPES.HORIZONTAL]: 0,
  [PRODUCT_ASPECT_TYPES.BOX_HORIZONTAL]: 1,
  [PRODUCT_ASPECT_TYPES.SQUARE]: 2,
  [PRODUCT_ASPECT_TYPES.BOX_VERTICAL]: 3,
  [PRODUCT_ASPECT_TYPES.VERTICAL]: 4,
}

export const TOP_PLACEMENT_STYLE = {
  OVERLAP: 'overlap',
  TOUCH: 'touch',
  GAP: 'gap',
}

export function getWidthSplitWithRotationPenaltyFor2Products(aspect1: number, aspect2: number, gapFraction: number = 0, img1TiltAngleRad: number = 0, img2TiltAngleRad: number = 0, minShare: number = 0.35, maxShare: number = 0.65) {
  const abscos = Math.abs(Math.cos(img2TiltAngleRad))
  const abssin = Math.abs(Math.sin(img2TiltAngleRad))

  const compressed1 = Math.sqrt(aspect1)
  const compressed2 = Math.sqrt(aspect2)
  const totalCompressed = compressed1 + compressed2
  let rawShare1 = compressed1 / totalCompressed
  let rawShare2 = compressed2 / totalCompressed

  if (img1TiltAngleRad !== 0) {
    const rotationWidthPenalty = abscos + abssin / aspect1
    rawShare1 *= rotationWidthPenalty
  }
  if (img2TiltAngleRad !== 0) {
    const rotationWidthPenalty = abscos + abssin / aspect2
    rawShare2 *= rotationWidthPenalty
  }

  const total = rawShare1 + rawShare2
  rawShare1 /= total
  rawShare2 /= total

  const clampedShare1 = Math.min(maxShare, Math.max(minShare, rawShare1))
  const clampedShare2 = 1 - clampedShare1

  const usable = 1 - gapFraction
  return {
    share1: clampedShare1 * usable,
    share2: clampedShare2 * usable,
  }
}

export function getWidthSplitWithRotationPenaltyFor3Products(aspect1: number, aspect2: number, aspect3: number, img1TiltAngleRad: number = 0, img2TiltAngleRad: number = 0, img3TiltAngleRad: number = 0, minShare: number = 0.20, maxShare: number = 0.50) {
  let rawShare2 = Math.sqrt(aspect2)
  let rawShare3 = Math.sqrt(aspect3)
  let rawShare1 = Math.sqrt(aspect1)

  // Rotation width penalty: a tilted image's bbox width = w·cos + h·sin > w,
  // so the scale formula gives it less visual size per unit of share.
  // Boost its share by the penalty factor (cos + sin/aspect) to compensate.
  if (img1TiltAngleRad !== 0) {
    const abscos1 = Math.abs(Math.cos(img1TiltAngleRad))
    const abssin1 = Math.abs(Math.sin(img1TiltAngleRad))
    rawShare1 *= (abscos1 + abssin1 / aspect1)
  }
  if (img2TiltAngleRad !== 0) {
    const abscos2 = Math.abs(Math.cos(img2TiltAngleRad))
    const abssin2 = Math.abs(Math.sin(img2TiltAngleRad))
    rawShare2 *= (abscos2 + abssin2 / aspect2)
  }
  if (img3TiltAngleRad !== 0) {
    const abscos3 = Math.abs(Math.cos(img3TiltAngleRad))
    const abssin3 = Math.abs(Math.sin(img3TiltAngleRad))
    rawShare3 *= (abscos3 + abssin3 / aspect3)
  }

  // Renormalise after penalties
  const total = rawShare1 + rawShare2 + rawShare3
  rawShare1 /= total
  rawShare2 /= total
  rawShare3 /= total

  // Clamp: no product dominates too much (tighter than 2-product bounds)
  const clamped1 = Math.min(maxShare, Math.max(minShare, rawShare1))
  const clamped2 = Math.min(maxShare, Math.max(minShare, rawShare2))
  const clamped3 = Math.min(maxShare, Math.max(minShare, rawShare3))

  const clampedTotal = clamped1 + clamped2 + clamped3
  return {
    share1: clamped1 / clampedTotal,
    share2: clamped2 / clampedTotal,
    share3: clamped3 / clampedTotal,
  }
}

export function getHeightSplitForPairWithRotationPenalty(aspect1: number, aspect2: number, img1TiltAngleRad: number = 0, img2TiltAngleRad: number = 0, minShare: number = 0.4, maxShare: number = 0.6) {
  const abscos1 = Math.abs(Math.cos(img1TiltAngleRad))
  const abssin1 = Math.abs(Math.sin(img1TiltAngleRad))
  const abscos2 = Math.abs(Math.cos(img2TiltAngleRad))
  const abssin2 = Math.abs(Math.sin(img2TiltAngleRad))

  // For height, taller images (lower aspect ratio) need more space, 
  // so we use the inverse of the square root of the aspect ratio.
  const compressed1 = 1 / Math.sqrt(aspect1)
  const compressed2 = 1 / Math.sqrt(aspect2)
  const totalCompressed = compressed1 + compressed2

  let rawShare1 = compressed1 / totalCompressed
  let rawShare2 = compressed2 / totalCompressed

  // Rotation height penalty: 
  // Bounding box height multiplier = |cos(θ)| + aspect * |sin(θ)|
  if (img1TiltAngleRad !== 0) {
    const rotationHeightPenalty1 = abscos1 + aspect1 * abssin1
    rawShare1 *= rotationHeightPenalty1
  }

  if (img2TiltAngleRad !== 0) {
    const rotationHeightPenalty2 = abscos2 + aspect2 * abssin2
    rawShare2 *= rotationHeightPenalty2
  }

  // Re-normalize after applying penalties
  if (img1TiltAngleRad !== 0 || img2TiltAngleRad !== 0) {
    const total = rawShare1 + rawShare2
    rawShare1 /= total
    rawShare2 /= total
  }

  // Clamp: neither image dominates more than 60% of the space
  const clampedShare1 = Math.min(maxShare, Math.max(minShare, rawShare1))
  const clampedShare2 = 1 - clampedShare1

  return {
    heightShare1: clampedShare1,
    heightShare2: clampedShare2,
  }
}

export function getOverlapPercentageForStaircase(aspect1: string, aspect2: string, aspect3: string) {
  const overlapFractions = [0.35, 0.35]
  if (aspect1 === PRODUCT_ASPECT_TYPES.HORIZONTAL) overlapFractions[0] = 0.40
  return { overlapFraction1: overlapFractions[0], overlapFraction2: overlapFractions[1] }
}

export function determineProductsPlacementStyleInTopPlacement(aspect1: string, aspect2: string, placementAreaWidth: number, placementAreaHeight: number) {
  const areaAspect = placementAreaWidth / placementAreaHeight

  // Vertical placement area → always overlap
  if (areaAspect < 0.7) return TOP_PLACEMENT_STYLE.OVERLAP

  // Squarish or horizontal placement area — decide based on product aspect types
  if (aspect1 === PRODUCT_ASPECT_TYPES.VERTICAL || aspect2 === PRODUCT_ASPECT_TYPES.VERTICAL) {
    return TOP_PLACEMENT_STYLE.GAP
  }

  if (aspect1 === PRODUCT_ASPECT_TYPES.BOX_VERTICAL && aspect2 === PRODUCT_ASPECT_TYPES.BOX_VERTICAL) {
    return TOP_PLACEMENT_STYLE.TOUCH
  }

  return TOP_PLACEMENT_STYLE.OVERLAP
}

export function getOverlapPercentageForTopPlacement(aspect1: string, aspect2: string) {
  if ([aspect1, aspect2].includes(PRODUCT_ASPECT_TYPES.HORIZONTAL)) return 25
  if ([aspect1, aspect2].includes(PRODUCT_ASPECT_TYPES.BOX_HORIZONTAL)) return 20
  if ([aspect1, aspect2].includes(PRODUCT_ASPECT_TYPES.SQUARE)) return 15
  return 10
}